import { useEffect, useMemo, useState } from "react";
import type { LocalStore, StoredGame } from "@onchainreps/storage";
import { apiRequest, type SyncedGame } from "../lib/api";
import { useAuth } from "../lib/useAuth";
import { ProgressPanel } from "./ProgressPanel";

interface GamesDashboardProps {
  store: LocalStore | null;
  onOpen: (pgn: string) => void;
  onOpenDrills: () => void;
}

function playerAccuracy(game: StoredGame): number | null {
  if (game.playerColor === "white") return game.accuracyWhite;
  if (game.playerColor === "black") return game.accuracyBlack;
  return null;
}

function playerResult(game: StoredGame): string | null {
  if (!game.playerColor) return null;
  const won = game.playerColor === "white" ? "1-0" : "0-1";
  const lost = game.playerColor === "white" ? "0-1" : "1-0";
  if (game.result === won) return "W";
  if (game.result === lost) return "L";
  if (game.result === "1/2-1/2") return "D";
  return null;
}

export function GamesDashboard({ store, onOpen, onOpenDrills }: GamesDashboardProps) {
  const auth = useAuth();
  const [games, setGames] = useState<StoredGame[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [syncedGames, setSyncedGames] = useState<SyncedGame[]>([]);
  const [syncedError, setSyncedError] = useState<string | null>(null);

  const refresh = async () => {
    if (!store) return;
    const rows = await store.listGames();
    setGames(rows);
    setLoaded(true);
  };

  useEffect(() => {
    void refresh();
  }, [store]);

  useEffect(() => {
    const token = auth.session?.access_token;
    if (!token) {
      setSyncedGames([]);
      return;
    }
    let active = true;
    void apiRequest<{ games: SyncedGame[] }>("/api/games", { token }).then((result) => {
      if (!active) return;
      if (result.error) {
        setSyncedError(result.error);
        return;
      }
      setSyncedError(null);
      setSyncedGames(result.data?.games ?? []);
    });
    return () => {
      active = false;
    };
  }, [auth.session?.access_token]);

  const stats = useMemo(() => {
    const rated = games.filter((game) => playerAccuracy(game) !== null);
    const accuracy = rated.length
      ? rated.reduce((sum, game) => sum + (playerAccuracy(game) ?? 0), 0) / rated.length
      : 0;
    let wins = 0;
    let losses = 0;
    let draws = 0;
    let attributed = 0;
    for (const game of games) {
      const outcome = playerResult(game);
      if (outcome === null) continue;
      attributed += 1;
      if (outcome === "W") wins += 1;
      else if (outcome === "L") losses += 1;
      else if (outcome === "D") draws += 1;
    }
    return {
      accuracy: Math.round(accuracy * 10) / 10,
      wins,
      losses,
      draws,
      rated: rated.length,
      attributed,
    };
  }, [games]);

  const worstOpening = useMemo(() => {
    const byOpening = new Map<string, { sum: number; count: number }>();
    for (const game of games) {
      const accuracy = playerAccuracy(game);
      if (accuracy === null || !game.opening) continue;
      const entry = byOpening.get(game.opening) ?? { sum: 0, count: 0 };
      entry.sum += accuracy;
      entry.count += 1;
      byOpening.set(game.opening, entry);
    }
    let worst: { opening: string; accuracy: number } | null = null;
    for (const [opening, entry] of byOpening) {
      if (entry.count === 0) continue;
      const accuracy = entry.sum / entry.count;
      if (!worst || accuracy < worst.accuracy) worst = { opening, accuracy };
    }
    return worst;
  }, [games]);

  if (!store) {
    return (
      <section className="panel">
        <div className="panel__head">
          <h1 className="panel__title">Your games</h1>
        </div>
        <div className="panel__body">
          <p className="notice">Opening local storage…</p>
        </div>
      </section>
    );
  }

  return (
    <div className="dashboard">
      <ProgressPanel store={store} games={games} onOpenDrills={onOpenDrills} />

      <section className="panel">
        <div className="panel__head">
          <h1 className="panel__title">Your games</h1>
          <span className="chip">
            <span className="chip__dot" />
            {games.length} analysed
          </span>
        </div>
        <div className="panel__body">
          {syncedGames.length ? (
            <div className="synced">
              <h3 className="synced__title">From your linked accounts</h3>
              <ul className="gamelist">
                {syncedGames.map((game) => (
                  <li className="gamelist__row" key={game.id}>
                    <button className="gamelist__main" onClick={() => onOpen(game.pgn)}>
                      <span className="gamelist__names">
                        {game.white ?? "White"} vs {game.black ?? "Black"}
                      </span>
                      <span className="gamelist__meta">
                        {game.source} · {game.opening ?? game.eco ?? "Unclassified opening"}
                        {game.played_at ? ` · ${game.played_at.slice(0, 10)}` : ""}
                      </span>
                    </button>
                    <span className="gamelist__result">{game.result ?? "*"}</span>
                    <span className="gamelist__accuracy">
                      {game.accuracy_white !== null && game.accuracy_black !== null
                        ? `${Math.round(game.accuracy_white)}/${Math.round(game.accuracy_black)}`
                        : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : syncedError ? (
            <p className="notice notice--error">{syncedError}</p>
          ) : null}

          {games.length === 0 ? (
            <p className="notice">
              Nothing here yet. Analyse a game and it is saved on this device, along with the positions
              where you gave away the evaluation.
            </p>
          ) : (
            <>
              <div className="metrics">
                <div className="metric">
                  <span className="metric__label">Average accuracy</span>
                  <span className="metric__value metric__value--white">
                    {stats.rated > 0 ? `${stats.accuracy}%` : "—"}
                  </span>
                  <span className="metric__note">your games</span>
                </div>
                <div className="metric">
                  <span className="metric__label">Record</span>
                  <span className="metric__value">
                    {stats.attributed > 0 ? `${stats.wins}–${stats.draws}–${stats.losses}` : "—"}
                  </span>
                  <span className="metric__note">
                    {stats.attributed > 0 ? "wins–draws–losses" : "import with a username"}
                  </span>
                </div>
                <div className="metric">
                  <span className="metric__label">Costliest opening</span>
                  <span className="metric__value" style={{ fontSize: "var(--text-md)" }}>
                    {worstOpening ? `${Math.round(worstOpening.accuracy)}%` : "—"}
                  </span>
                  <span className="metric__note">{worstOpening?.opening ?? "no data yet"}</span>
                </div>
              </div>

              <ul className="gamelist">
                {games.map((game) => {
                  const accuracy = playerAccuracy(game);
                  const outcome = playerResult(game);
                  return (
                    <li className="gamelist__row" key={game.id}>
                      <button className="gamelist__main" onClick={() => onOpen(game.pgn)}>
                        <span className="gamelist__names">
                          {game.white} vs {game.black}
                        </span>
                        <span className="gamelist__meta">
                          {game.opening ?? game.eco ?? "Unclassified opening"}
                          {game.playedAt ? ` · ${game.playedAt.slice(0, 10)}` : ""}
                        </span>
                      </button>
                      <span className="gamelist__result">
                        {outcome ?? game.result}
                      </span>
                      <span className="gamelist__accuracy">
                        {accuracy === null
                          ? game.accuracyWhite === null
                            ? "—"
                            : `${Math.round(game.accuracyWhite)}/${Math.round(game.accuracyBlack ?? 0)}`
                          : `${Math.round(accuracy)}%`}
                      </span>
                      <button
                        className="iconbtn iconbtn--quiet"
                        aria-label="Delete game"
                        onClick={() => {
                          void store.deleteGame(game.id).then(() => void refresh());
                        }}
                      >
                        ×
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
